import vi from './vi';
import en from './en';
import moment from 'moment';
import 'moment/locale/vi';

export type Language = 'vi' | 'en';

const STORAGE_KEY = 'app_language';
const CHANGE_EVENT = 'languageChange';
const DEFAULT_LANGUAGE: Language = 'vi';

type Translations = typeof vi;
const translations: Record<Language, Translations> = { vi, en };

function normalizeLanguage(lang: string | null | undefined): Language {
    return lang === 'en' ? 'en' : DEFAULT_LANGUAGE;
}

function applyLanguageSideEffects(lang: Language): void {
    document.documentElement.lang = lang;
    moment.locale(lang === 'vi' ? 'vi' : 'en-gb');
}

export function initializeLanguage(): Language {
    const lang = normalizeLanguage(localStorage.getItem(STORAGE_KEY));
    localStorage.setItem(STORAGE_KEY, lang);
    applyLanguageSideEffects(lang);
    return lang;
}

/** Returns the currently active language (default: 'vi'). */
export function getLanguage(): Language {
    return normalizeLanguage(localStorage.getItem(STORAGE_KEY));
}

/** Switches the active language and notifies all subscribers. */
export function setLanguage(lang: Language): void {
    const nextLanguage = normalizeLanguage(lang);
    localStorage.setItem(STORAGE_KEY, nextLanguage);
    applyLanguageSideEffects(nextLanguage);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: nextLanguage }));
}

/** Translates a key using the current language, falling back to the key itself. */
export function t(key: keyof Translations): string {
    const lang = getLanguage();
    return translations[lang][key] ?? translations['vi'][key] ?? key;
}

/**
 * Subscribe a component to language-change events.
 * Call this inside componentDidMount and return the unsubscribe fn to call in componentWillUnmount.
 *
 * @example
 *   componentDidMount() {
 *     this.unsubscribeLang = subscribeLanguage(() => this.forceUpdate());
 *   }
 *   componentWillUnmount() {
 *     this.unsubscribeLang?.();
 *   }
 */
export function subscribeLanguage(callback: () => void): () => void {
    const handler = () => callback();
    window.addEventListener(CHANGE_EVENT, handler);
    return () => window.removeEventListener(CHANGE_EVENT, handler);
}

export function getDateLocale(lang: Language = getLanguage()): string {
    return lang === 'vi' ? 'vi-VN' : 'en-US';
}

export function getCalendarLocale(lang: Language = getLanguage()): string {
    return lang === 'vi' ? 'vi' : 'en-gb';
}

export function formatLocalizedDate(
    date: Date,
    options: Intl.DateTimeFormatOptions,
    lang: Language = getLanguage()
): string {
    return date.toLocaleDateString(getDateLocale(lang), options);
}

export function formatMonthYear(date: Date, lang: Language = getLanguage()): string {
    return formatLocalizedDate(date, { month: 'long', year: 'numeric' }, lang);
}

export function formatShortDate(date: Date, lang: Language = getLanguage()): string {
    return formatLocalizedDate(date, { weekday: 'short', month: 'short', day: '2-digit' }, lang);
}

export function formatLongDate(date: Date, lang: Language = getLanguage()): string {
    return formatLocalizedDate(date, { weekday: 'long', month: 'short', day: '2-digit' }, lang);
}

